// components/floating/PhoneButton.tsx
"use client";
import Image from "next/image";
import Link from "next/link";
import { QrCode } from "lucide-react";
import { useState } from "react";
import { Button } from "../../shadcn/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../shadcn/tooltip";
import { FloatingMenuItem } from "@/app/lib/features/floatingContent/floatingContent.types";
import { trackGeneric } from "@/app/lib/tracking/interactions";

interface PhoneButtonProps {
  item: FloatingMenuItem;
}

const PhoneButton = ({ item }: PhoneButtonProps) => {
  const modal = item.app_promotion_modal;
  const [isTouchDevice] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(hover: none)").matches,
  );
  const [open, setOpen] = useState(false);

  return (
    <Tooltip open={isTouchDevice ? open : undefined}>
      <TooltipTrigger asChild>
        <Button
          type="button"
          className="w-10 sm:w-11 lg:w-12 h-10 sm:h-11 lg:h-12 bg-black hover:bg-neutral-800 rounded-xl p-0 shadow-md transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5"
          style={{ backgroundColor: "#f2791c" }}
          onClick={isTouchDevice ? () => setOpen((prev) => !prev) : undefined}
        >
          <Image
            src={item.icon}
            alt={item.title}
            width={28}
            height={28}
            className="object-contain"
          />
        </Button>
      </TooltipTrigger>
      <TooltipContent
        side="left"
        variant="white"
        className="p-0 overflow-hidden rounded-2xl shadow-xl border border-gray-100 w-72 sm:w-80"
      >
        {modal ? (
          <div className="p-5 space-y-4">
            <div className="text-center space-y-1">
              <p className="text-sm text-gray-500 font-medium">{modal.title}</p>
              <p
                className="text-2xl font-extrabold tracking-tight"
                style={{ color: modal.color }}
              >
                {modal.discount_text}
              </p>
              <p className="text-sm text-gray-400">{modal.subtitle}</p>
            </div>

            <div className="h-px bg-gray-100" />

            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-col gap-2 flex-1">
                {modal.apps.map((app) => (
                  <Button
                    key={app.key}
                    variant="outline"
                    size="sm"
                    className="justify-start gap-2 rounded-lg h-9"
                    asChild
                  >
                    <Link href={app.url} target="_blank" onClick={() => trackGeneric(app.key, "click")}>
                      <Image
                        src={app.icon}
                        alt={app.title}
                        width={16}
                        height={16}
                      />
                      <span className="text-black text-xs font-medium">
                        {app.title}
                      </span>
                    </Link>
                  </Button>
                ))}
              </div>

              <div className="w-20 h-20 bg-gray-50 rounded-xl border border-gray-200 flex items-center justify-center shrink-0 overflow-hidden">
                {modal.qr_code_icon ? (
                  <Image
                    src={modal.qr_code_icon}
                    alt="QR Code"
                    width={72}
                    height={72}
                    className="object-contain"
                  />
                ) : (
                  <QrCode className="w-14 h-14 text-black" />
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="p-3">
            <p className="text-sm">{item.title}</p>
          </div>
        )}
      </TooltipContent>
    </Tooltip>
  );
};

export default PhoneButton;
